import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Label,
} from "recharts";
import ThinScroll from "../shared/ThinScroll";
import ThemedChartTooltip from "../shared/ThemedChartTooltip";
import { getCiTrends } from "../../api/competitiveIntelligence";
import { RefetchBar } from "./AnalysisPanel";
import { SkeletonText } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import SelectMenu from "../shared/SelectMenu";
import Pagination from "../shared/Pagination";
import { useTheme } from "../../contexts/ThemeContext";
import { MONTHS, fmtRate, SERIES_HUES } from "./shared";

const PAGE_SIZE = 15;

const fmtTickDate = (iso) => {
  if (!iso) return "";
  const [, month, day] = iso.split("-");
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
};
const fmtFullDate = (iso) => {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

// Lookback thresholds offered in the dropdown, in ascending order. The
// "1 month" option is always shown (it is the default and the minimum
// granularity); longer lookbacks only appear once enough history exists.
const LOOKBACK_THRESHOLDS = [1, 3, 6, 12];
const LOOKBACK_LABELS = { 1: "1 month", 3: "3 months", 6: "6 months", 12: "12 months" };
// Request a lookback wide enough to always retrieve the full available
// history; the selected lookback then filters client-side (see below).
const FULL_LOOKBACK_MONTHS = 120;
const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_MONTH = MS_PER_DAY * 30.4375;

// Derive dropdown options from the actual span of the loaded data so options
// only appear when enough history exists and new options show up automatically
// as sync data accumulates.
const deriveMonthOptions = (rows) => {
  const dates = rows.map((r) => r.metric_date).filter(Boolean).sort();
  if (dates.length === 0) {
    return [{ value: 1, label: LOOKBACK_LABELS[1] }];
  }
  const spanMonths = (new Date(dates[dates.length - 1]) - new Date(dates[0])) / MS_PER_MONTH;
  return LOOKBACK_THRESHOLDS
    .filter((n) => n === 1 || spanMonths >= n)
    .map((n) => ({ value: n, label: LOOKBACK_LABELS[n] }));
};

// Filter the full history to the selected lookback window, anchored to the
// most recent data point so every selected option renders real points (no
// flat-line gaps for months that have no data).
const filterByLookback = (rows, months) => {
  if (!rows.length || months == null) return rows;
  const dates = rows.map((r) => r.metric_date).filter(Boolean).sort();
  if (!dates.length) return rows;
  const cutoff = new Date(new Date(dates[dates.length - 1]).getTime() - months * MS_PER_MONTH);
  return rows.filter((r) => !r.metric_date || new Date(r.metric_date) >= cutoff);
};

const PanelHeader = ({ months, onMonthsChange, categoryLabel, pointCount, options }) => (
  <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3 flex-wrap">
    <div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
        Rate Trends
        <InfoTip text="Historical line chart of your average rate vs. comp set average and tier medians (hotel, Airbnb). Each nightly sync adds a new data point; needs a few syncs before trends become readable." />
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        Your rate vs. comp-set average and tier medians over time. Adds a data point with each sync.
      </p>
    </div>
    <div className="flex items-center gap-3 flex-wrap">
      {categoryLabel && (
        <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
          {categoryLabel}
        </span>
      )}
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {pointCount} {pointCount === 1 ? "point" : "points"}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-sm text-gray-600 dark:text-gray-300">Lookback:</span>
        <SelectMenu
          ariaLabel="Lookback window"
          value={months}
          onChange={onMonthsChange}
          options={options}
        />
      </span>
    </div>
  </div>
);

const Skeleton = () => (
  <div className="p-5">
    <SkeletonText lines={6} />
  </div>
);

const Empty = ({ message }) => (
  <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">{message}</div>
);

// Spec palette: client (hero) = blue, compSet aggregate = emerald (mirrors
// "Market" in 2-series charts), hotel = neutral gray (Hotel tier blue would
// collide with client), airbnb = spec pink. Hues from shared SERIES_HUES.
const SERIES_COLORS = {
  client: SERIES_HUES.blue,
  compSet: SERIES_HUES.emerald,
  hotel: SERIES_HUES.gray,
  airbnb: SERIES_HUES.pink,
};

const computeYDomain = (rows, hasHotel, hasAirbnb) => {
  const vals = [];
  rows.forEach((row) => {
    if (row.client_avg_rate != null) vals.push(row.client_avg_rate);
    if (row.comp_set_avg_rate != null) vals.push(row.comp_set_avg_rate);
    if (hasHotel && row.hotel_median_rate != null) vals.push(row.hotel_median_rate);
    if (hasAirbnb && row.airbnb_median_rate != null) vals.push(row.airbnb_median_rate);
  });
  if (vals.length === 0) return [0, "auto"];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = Math.max((max - min) * 0.1, 5);
  return [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
};

const LegendSwatch = ({ color, label, dashed }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
    <svg width="18" height="8" aria-hidden="true">
      <line
        x1="0" y1="4" x2="18" y2="4"
        stroke={color}
        strokeWidth={dashed ? 2 : 2.5}
        strokeDasharray={dashed ? "3 3" : undefined}
      />
      <circle cx="9" cy="4" r="2.5" fill="#fff" stroke={color} strokeWidth="1.5" />
    </svg>
    <span style={{ color }}>{label}</span>
  </span>
);

const TrendsChart = ({ rows, hasHotel, hasAirbnb }) => {
  // Recharts doesn't reliably inherit Tailwind's `dark:` text color into SVG
  // tick <text> nodes (currentColor resolves to black in dark mode), so drive
  // the axis/label color explicitly from the theme — same approach the file's
  // ThemedChartTooltip already uses for dark-aware chart styling.
  const { isDarkMode } = useTheme();
  const axisColor = isDarkMode ? "#9ca3af" : "#6b7280"; // gray-400 / gray-500
  return (
  <div>
    <div className="w-full h-[200px] sm:h-[240px] md:h-[280px] lg:h-[300px] rotate-x-labels">
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
          <XAxis
            dataKey="metric_date"
            tickFormatter={fmtTickDate}
            tick={{ fill: axisColor, fontSize: 12 }}
          />
          <YAxis
            domain={computeYDomain(rows, hasHotel, hasAirbnb)}
            tickFormatter={(value) => `$${value}`}
            tick={{ fill: axisColor, fontSize: 12 }}
          >
            <Label
              value="Rate (USD)"
              angle={-90}
              position="insideLeft"
              offset={10}
              style={{ textAnchor: "middle", fill: axisColor, fontSize: 12 }}
            />
          </YAxis>
          <ThemedChartTooltip
            labelFormatter={(date) => fmtFullDate(date)}
            formatter={(value) => fmtRate(value)}
            itemSorter={(item) => ({ client_avg_rate: 0, comp_set_avg_rate: 1, hotel_median_rate: 2, airbnb_median_rate: 3 }[item.dataKey] ?? 99)}
          />
          <Line
            type="monotone"
            dataKey="client_avg_rate"
            name="Your Rate"
            stroke={SERIES_COLORS.client}
            strokeWidth={2.5}
            dot={{ r: 3 }}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="comp_set_avg_rate"
            name="Market Avg"
            stroke={SERIES_COLORS.compSet}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={{ r: 3 }}
            connectNulls
          />
          {hasHotel && (
            <Line
              type="monotone"
              dataKey="hotel_median_rate"
              name="Hotel Median"
              stroke={SERIES_COLORS.hotel}
              strokeWidth={2}
              strokeDasharray="2 3"
              dot={{ r: 3 }}
              connectNulls
            />
          )}
          {hasAirbnb && (
            <Line
              type="monotone"
              dataKey="airbnb_median_rate"
              name="Airbnb Median"
              stroke={SERIES_COLORS.airbnb}
              strokeWidth={2}
              strokeDasharray="2 3"
              dot={{ r: 3 }}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
      <LegendSwatch color={SERIES_COLORS.client} label="Your Rate" />
      <LegendSwatch color={SERIES_COLORS.compSet} label="Market Avg" dashed />
      {hasHotel && <LegendSwatch color={SERIES_COLORS.hotel} label="Hotel Median" dashed />}
      {hasAirbnb && <LegendSwatch color={SERIES_COLORS.airbnb} label="Airbnb Median" dashed />}
    </div>
  </div>
  );
};

const TrendsTable = ({ rows, hasHotel, hasAirbnb }) => (
  <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
    <table className="w-full text-sm responsive-card-table">
      <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <th className="px-3 py-2.5 text-left font-semibold">Date</th>
          <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
          <th className="px-3 py-2.5 text-right font-semibold">Market Avg</th>
          <th className="px-3 py-2.5 text-right font-semibold">Market Median</th>
          {hasHotel && <th className="px-3 py-2.5 text-right font-semibold">Hotel Median</th>}
          {hasAirbnb && <th className="px-3 py-2.5 text-right font-semibold">Airbnb Median</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${row.metric_date}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
            <td data-label="Date" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtFullDate(row.metric_date)}</td>
            <td data-label="Your Rate" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: SERIES_COLORS.client }}>{fmtRate(row.client_avg_rate)}</td>
            <td data-label="Market Avg" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: SERIES_COLORS.compSet }}>{fmtRate(row.comp_set_avg_rate)}</td>
            <td data-label="Market Median" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.comp_set_median_rate)}</td>
            {hasHotel && <td data-label="Hotel Median" className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: SERIES_COLORS.hotel }}>{fmtRate(row.hotel_median_rate)}</td>}
            {hasAirbnb && <td data-label="Airbnb Median" className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: SERIES_COLORS.airbnb }}>{fmtRate(row.airbnb_median_rate)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </ThinScroll>
);

const RateTrendsPanel = ({ clientId, categoryCode }) => {
  const [months, setMonths] = useState(1);
  const [state, setState] = useState({ loading: false, error: "", data: null });
  // Pagination for the date table (the chart above is not paginated). Resets
  // to page 1 whenever the lookback-filtered rows change — see the effect below.
  const [page, setPage] = useState(1);

  // The API accepts category_code='all' to mean "across all categories"; mirror that
  // when the user has no category tab selected, since this is an account-level chart.
  const apiCategory = categoryCode || "all";

  // Fetch the full available history once per client/category. The lookback
  // selector filters client-side (see `rows` below) so options can be derived
  // from the real data range and no lookback produces flat-line gaps.
  useEffect(() => {
    let cancelled = false;
    // Keep prior `data` during refetch so the chart/table don't flash to a
    // skeleton when the user changes the lookback selector — RefetchBar +
    // dim body indicates the refresh instead.
    setState((prev) => ({ loading: true, error: "", data: prev.data }));
    getCiTrends(clientId, { categoryCode: apiCategory, months: FULL_LOOKBACK_MONTHS })
      .then((resp) => {
        if (cancelled) return;
        const payload = resp?.data ?? resp ?? [];
        setState({ loading: false, error: "", data: Array.isArray(payload) ? payload : [] });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err?.message || "Failed to load", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, apiCategory]);

  const allRows = useMemo(
    () => (state.data || [])
      .slice()
      .sort((rowA, rowB) => (rowA.metric_date || "").localeCompare(rowB.metric_date || "")),
    [state.data]
  );
  const monthOptions = useMemo(() => deriveMonthOptions(allRows), [allRows]);
  // Clamp the selected lookback to an available option so the dropdown never
  // shows a blank value (e.g. after switching to a category with less data).
  const selectedMonths = monthOptions.some((o) => o.value === months)
    ? months
    : (monthOptions[0]?.value ?? 1);
  const rows = useMemo(() => filterByLookback(allRows, selectedMonths), [allRows, selectedMonths]);
  const hasHotel = rows.some((row) => row.hotel_median_rate != null);
  const hasAirbnb = rows.some((row) => row.airbnb_median_rate != null);
  const categoryLabel = apiCategory === "all" ? "ALL" : apiCategory.toUpperCase();

  // Client-side pagination of the date table at PAGE_SIZE rows per page,
  // mirroring the Rate Calendar pattern. Snap back to page 1 whenever the row
  // set changes: a lookback switch produces a new `rows` reference, so this
  // also resets pagination when the lookback selection changes.
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [rows]);
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
      <PanelHeader
        months={selectedMonths}
        onMonthsChange={setMonths}
        categoryLabel={categoryLabel}
        pointCount={rows.length}
        options={monthOptions}
      />
      {state.loading && state.data == null && !state.error && <Skeleton />}
      {state.error && (
        <ErrorState variant="inset" message={state.error} />
      )}
      {!state.error && state.data != null && rows.length === 0 && (
        <Empty message="No trend data yet. Points accumulate as sync cycles run." />
      )}
      {!state.error && state.data != null && rows.length > 0 && (
        <div className="relative">
          {state.loading && <RefetchBar />}
          <div
            className={`p-5 space-y-3 md:space-y-5 ${state.loading ? "opacity-60 pointer-events-none transition-opacity" : "transition-opacity"}`}
            aria-busy={state.loading}
          >
            <TrendsChart rows={rows} hasHotel={hasHotel} hasAirbnb={hasAirbnb} />
            <TrendsTable rows={pageRows} hasHotel={hasHotel} hasAirbnb={hasAirbnb} />
            {totalPages > 1 && (
              <div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalItems={total}
                  perPage={PAGE_SIZE}
                  label="dates"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RateTrendsPanel;
